import { getDocument } from '@atlaskit/browser-apis';
import { useThemeObserver } from '@atlaskit/tokens';
import { useFirePostOfficeTrigger } from '@atlassian/post-office-context';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
// @ts-expect-error: 'React' is declared but its value is never read
// React import required to render in Jira SSR
import React, { useCallback, useEffect, useState } from 'react';

import { DetailViewSpotlight } from './DetailViewSpotlight';
import { HighImpactModal } from './HighImpactModal';
import { HighImpactSpotlightTour } from './HighImpactSpotlightTour';
import ListMergeSpotlightIllustrationDark from '../assets/list-merge-spotlight-dark.gif';
import ListMergeSpotlightIllustration from '../assets/list-merge-spotlight-light.gif';
import { COPY_LIST_SETTINGS_YES } from '../placements/in-app-v2/issue-navigator-placement/constants';

type OnboardingStep = 'detail-view-spotlight' | 'modal' | 'spotlight-tour' | 'dismissed';

type HighImpactOnboardingProps = {
	hasListPreference?: boolean;
	isDetailedView?: boolean;
};

export const HighImpactOnboarding = ({
	hasListPreference,
	isDetailedView,
}: HighImpactOnboardingProps) => {
	const [currentStep, setCurrentStep] = useState<OnboardingStep>(
		isDetailedView ? 'detail-view-spotlight' : 'modal',
	);
	const [copyListSettings, setCopyListSettings] = useState(COPY_LIST_SETTINGS_YES);
	const { messageInstanceClicked } = useMessageInstanceEvent();

	const dismissMessage = useCallback(
		(isTourCompleted = false) => {
			setCurrentStep('dismissed');
			void messageInstanceClicked({
				action: {
					actionType: 'dismiss',
				},
				additionalMetadataFromFrontend: {},
				clickedElement: {
					buttonType: 'secondary',
					clickedElement: 'button',
					clickIdentifier: isTourCompleted ? 'tourCompleted' : 'dismiss',
				},
				messageEventType: 'clicked',
			});
		},
		[messageInstanceClicked],
	);

	const fireExploreEvent = useCallback(() => {
		let clickIdentifier = 'exploreFilters';
		if (hasListPreference) {
			clickIdentifier +=
				copyListSettings === COPY_LIST_SETTINGS_YES
					? 'WithCopyListSettings'
					: 'WithoutCopyListSettings';
		}

		void messageInstanceClicked({
			action: {
				actionType: 'open',
			},
			additionalMetadataFromFrontend: {},
			clickedElement: {
				buttonType: 'secondary',
				clickedElement: 'button',
				clickIdentifier,
			},
			messageEventType: 'clicked',
		});
	}, [messageInstanceClicked, copyListSettings]);

	useEffect(() => {
		const handleEscape = (ev: KeyboardEvent) => {
			if (ev.key === 'Escape') {
				dismissMessage();
			}
		};

		getDocument()?.addEventListener('keydown', handleEscape);

		return () => {
			getDocument()?.removeEventListener('keydown', handleEscape);
		};
	}, [dismissMessage]);

	const theme = useThemeObserver();

	const { fireTrigger: fireExploreListViewButtonTriggerWithCopyListSettings } =
		useFirePostOfficeTrigger({
			triggerId:
				'jira-nin-list-merge-modal-onboarding-explore-list-view-button-clicked-with-copy-list-settings',
		});
	const { fireTrigger: fireExploreListViewButtonTriggerWithoutCopyListSettings } =
		useFirePostOfficeTrigger({
			triggerId:
				'jira-nin-list-merge-modal-onboarding-explore-list-view-button-clicked-without-copy-list-settings',
		});

	const handleExploreListViewButtonClick = useCallback(() => {
		if (copyListSettings === COPY_LIST_SETTINGS_YES) {
			fireExploreListViewButtonTriggerWithCopyListSettings({ isActive: true });
		} else {
			fireExploreListViewButtonTriggerWithoutCopyListSettings({ isActive: true });
		}
		setCurrentStep('spotlight-tour');
		fireExploreEvent();
	}, [
		copyListSettings,
		fireExploreListViewButtonTriggerWithCopyListSettings,
		fireExploreListViewButtonTriggerWithoutCopyListSettings,
		fireExploreEvent,
	]);

	const isDarkMode = theme.colorMode === 'dark';

	const listMergeSpotlightIllustration = isDarkMode
		? ListMergeSpotlightIllustrationDark
		: ListMergeSpotlightIllustration;

	switch (currentStep) {
		case 'detail-view-spotlight':
			return (
				<DetailViewSpotlight
					setNextStep={() => setCurrentStep('spotlight-tour')}
					onDismissed={dismissMessage}
					illustration={listMergeSpotlightIllustration}
				/>
			);
		case 'modal':
			return (
				<HighImpactModal
					hasListPreference={hasListPreference}
					onClose={() => dismissMessage()}
					onExploreListViewButtonClick={handleExploreListViewButtonClick}
					copyListSettings={copyListSettings}
					setCopyListSettings={setCopyListSettings}
				/>
			);
		case 'spotlight-tour':
			return <HighImpactSpotlightTour onDismiss={dismissMessage} />;
		default:
			return null;
	}
};
